import { ChangeEvent, DragEvent, FC, ReactNode, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { colors, fontWeights, radii } from '../../theme/tokens';
import CameraCaptureDialog from '../../components/CameraCapture';
import { ACCEPT_ATTRIBUTE, MAX_MEGABYTES_PER_FILE, MAX_PAGES_PER_DOCUMENT } from './constants';
import PageList from './PageList';
import { PageRow } from './types';

// Step 1 — "Charger le fichier" (XD screen 3), as a page collector
// (ROADMAP.md, Part 3).
//
// With no pages, the XD empty state as-is: drop zone with a dashed
// orange border, upward arrow, "Déposez ici le document…", formats,
// maximum size (20 Mo, gap 6), "OU" and "Sélectionner le
// fichier". "Prendre une photo" is added (gap 5: scanning is kept),
// which opens camera capture.
//
// Mobile (smartphone XD, S3 in DESIGN.md): no drop zone — three big
// options stacked with white icons: "Prendre une photo", "Choisir une
// photo" (the photo library, its own input with `accept="image/*"`) and
// "Sélectionner un fichier". The uploaded-pages list below is ours: the
// artboard does not draw that state (gap M-2).
//
// With at least one page, the zone shrinks to an "Ajouter une page" bar
// with the two buttons, the "N page(s) (max. 10)" counter moves to the top right and
// the page list goes below.
//
// The "Pages" tab of "Modifier le document" reuses this same
// collector over an archived document, with `minPages={1}`.

type Props = {
  rows: PageRow[];
  busy: boolean;
  uploadsInProgress: boolean;
  minPages?: number;
  onAddFiles: (files: File[]) => void;
  onMove: (pageId: string, direction: -1 | 1) => void;
  onRemove: (pageId: string) => void;
  onRetry: (key: string) => void;
  onDismiss: (key: string) => void;
};

const Step1Pages: FC<Props> = ({
  rows,
  busy,
  uploadsInProgress,
  minPages,
  onAddFiles,
  onMove,
  onRemove,
  onRetry,
  onDismiss,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const inputRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const pageCount = rows.filter((row) => row.kind === 'page' || !row.error).length;
  const hasRows = rows.length > 0;
  const full = pageCount >= MAX_PAGES_PER_DOCUMENT;

  const pick = () => inputRef.current?.click();

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Emptied so that choosing the same file twice in a row counts.
    event.target.value = '';
    onAddFiles(files);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dragging) setDragging(true);
  };

  const onDragLeave = () => setDragging(false);

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    onAddFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT_ATTRIBUTE}
      multiple
      aria-label="Sélectionner le fichier"
      onChange={onInputChange}
      style={{ display: 'none' }}
    />
  );

  // The photo library of S3 ("Choisir une photo"): images only, so phones
  // offer the gallery. Mobile only — on desktop it would duplicate the file
  // picker, and the desktop wizard spec drives the single input.
  const galleryInput = (
    <input
      ref={galleryRef}
      type="file"
      accept="image/*"
      multiple
      aria-label="Choisir une photo"
      onChange={onInputChange}
      style={{ display: 'none' }}
    />
  );

  // A big S3 option: white icon over a white label, on the blue sheet.
  const mobileOption = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
  ) => (
    <ButtonBase
      onClick={onClick}
      disabled={full}
      focusRipple
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        p: 2,
        borderRadius: `${radii.card}px`,
        color: colors.white,
        opacity: full ? 0.5 : 1,
        '& svg': { fontSize: 48 },
      }}
    >
      {icon}
      <Typography sx={{ fontWeight: fontWeights.medium }}>{label}</Typography>
    </ButtonBase>
  );

  const selectButton = (compact: boolean) => (
    <Button
      variant="contained"
      color="secondary"
      onClick={pick}
      disabled={full}
      startIcon={compact ? <AddIcon /> : undefined}
      sx={compact ? { px: 2, py: 0.75 } : undefined}
    >
      {compact ? 'Ajouter une page' : 'Sélectionner le fichier'}
    </Button>
  );

  const galleryButton = (compact: boolean) => (
    <Button
      variant="outlined"
      color="inherit"
      onClick={() => galleryRef.current?.click()}
      disabled={full}
      startIcon={<PhotoLibraryOutlinedIcon />}
      sx={compact ? { px: 2, py: 0.75 } : undefined}
    >
      Choisir une photo
    </Button>
  );

  const cameraButton = (compact: boolean) => (
    <Button
      variant="outlined"
      color="inherit"
      onClick={() => setCameraOpen(true)}
      disabled={full}
      startIcon={<CameraAltOutlinedIcon />}
      sx={compact ? { px: 2, py: 0.75 } : undefined}
    >
      Prendre une photo
    </Button>
  );

  const dropzoneSx = {
    border: `2px dashed ${dragging ? colors.white : colors.accent}`,
    borderRadius: `${radii.card}px`,
    backgroundColor: dragging ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
    transition: 'background-color 120ms, border-color 120ms',
  } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {input}
      {isMobile && galleryInput}

      {!hasRows && isMobile ? (
        // S3: three big capture options instead of a drop zone.
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            py: 3,
          }}
        >
          {mobileOption('Prendre une photo', <AddAPhotoIcon />, () =>
            setCameraOpen(true),
          )}
          {mobileOption('Choisir une photo', <PhotoLibraryIcon />, () =>
            galleryRef.current?.click(),
          )}
          {mobileOption('Sélectionner un fichier', <FolderIcon />, pick)}
        </Box>
      ) : !hasRows ? (
        <Box
          data-testid="dropzone"
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          sx={{
            ...dropzoneSx,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 1,
            px: 3,
            py: 6,
          }}
        >
          <FileUploadOutlinedIcon sx={{ fontSize: 48, color: colors.accent }} />
          <Typography sx={{ fontWeight: fontWeights.medium }}>
            Déposez ici le document que vous souhaitez ajouter
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', opacity: 0.85 }}>
            Formats autorisés : PDF, PNG, JPEG
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', opacity: 0.85 }}>
            Taille max : {MAX_MEGABYTES_PER_FILE} Mo
          </Typography>

          <Typography
            sx={{
              my: 1.5,
              fontWeight: fontWeights.semiBold,
              letterSpacing: '0.1em',
              opacity: 0.85,
            }}
          >
            OU
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1.5,
              width: isMobile ? '100%' : 'auto',
            }}
          >
            {selectButton(false)}
            {cameraButton(false)}
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography component="h3" sx={{ fontWeight: fontWeights.semiBold }}>
              Pages du document
            </Typography>
            <Typography
              data-testid="page-counter"
              aria-live="polite"
              sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}
            >
              {pageCount} {pageCount > 1 ? 'pages' : 'page'} (max. {MAX_PAGES_PER_DOCUMENT})
            </Typography>
          </Box>

          <Box
            data-testid="dropzone"
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            sx={{
              ...dropzoneSx,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', opacity: 0.85 }}>
              {full
                ? `Ce document a atteint ${MAX_PAGES_PER_DOCUMENT} pages.`
                : 'Déposez une page ici, ou'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: isMobile ? 'column' : 'row' }}>
              {selectButton(true)}
              {isMobile && galleryButton(true)}
              {cameraButton(true)}
            </Box>
          </Box>

          <PageList
            rows={rows}
            disabled={busy || uploadsInProgress}
            minPages={minPages}
            onMove={onMove}
            onRemove={onRemove}
            onRetry={onRetry}
            onDismiss={onDismiss}
          />
        </>
      )}

      <CameraCaptureDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => onAddFiles([file])}
      />
    </Box>
  );
};

export default Step1Pages;
